import React, { useState, useEffect } from 'react';
import { 
    Users, Plus, Trash2, Save, RefreshCw, CheckCircle2, 
    Building2, Info, Loader2
} from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, DB_PATH } from '../../../config/firebase';
import { sincronizarEquipeInternaAcads } from '../../../services/alterdataService';
import Button from '../../../components/Button';

const DEFAULT_ACADS = [
    { clientId: "00001", label: "00001-UAC (Matriz)" },
    { clientId: "10033", label: "10033-UAC (Alcântara)" },
    { clientId: "10342", label: "10342-UAC (Niterói)" },
    { clientId: "10343", label: "10343-UAC (São Gonçalo)" },
    { clientId: "10413", label: "10413-UAC (Maricá)" },
    { clientId: "10480", label: "10480-UAC (Itaboraí)" },
    { clientId: "10576", label: "10576-UAC (Barra da Tijuca)" },
    { clientId: "10582", label: "10582-UAC (Cabo Frio)" },
    { clientId: "10589", label: "10589-UAC (Rio de Janeiro)" },
    { clientId: "30212", label: "30212-UAC (Campo Grande)" },
    { clientId: "30277", label: "30277-UAC (Nova Iguaçu)" },
    { clientId: "30501", label: "30501-UAC (Petrópolis)" }
];

export const InternalTeamTab = ({ isDarkMode, currentUser, onNavigateToLogs }) => {
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [savingConfig, setSavingConfig] = useState(false);
    const [selectedClientIds, setSelectedClientIds] = useState([]);
    const [clientLabels, setClientLabels] = useState({});
    
    // Novo item
    const [newClientId, setNewClientId] = useState('');
    const [newClientLabel, setNewClientLabel] = useState('');

    // Estado da sincronização
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncProgress, setSyncProgress] = useState(null);
    const [syncResult, setSyncResult] = useState(null);

    // Carregar configurações do Firestore
    useEffect(() => {
        const carregarConfig = async () => {
            try {
                setLoadingConfig(true);
                const ref = doc(db, `${DB_PATH}hub_settings`, 'internal_team_config');
                const snap = await getDoc(ref);
                if (snap.exists()) {
                    const data = snap.data();
                    setSelectedClientIds(data.selectedClientIds || DEFAULT_ACADS.map(a => a.clientId));
                    setClientLabels(data.clientLabels || DEFAULT_ACADS.reduce((acc, a) => ({ ...acc, [a.clientId]: a.label }), {}));
                } else {
                    // Inicializar com padrão
                    setSelectedClientIds(DEFAULT_ACADS.map(a => a.clientId));
                    setClientLabels(DEFAULT_ACADS.reduce((acc, a) => ({ ...acc, [a.clientId]: a.label }), {}));
                }
            } catch (err) {
                console.error("Erro ao carregar configurações de equipe interna:", err);
            } finally {
                setLoadingConfig(false);
            }
        };

        carregarConfig();
    }, []);

    // Adicionar novo ID de cliente
    const handleAddClient = () => {
        const cleanId = String(newClientId || '').trim();
        if (!cleanId) return alert('Digite o Código do Cliente (idDoCliente).');

        if (selectedClientIds.includes(cleanId)) {
            return alert(`O código de cliente ${cleanId} já está na lista de Equipe Interna.`);
        }

        const label = (newClientLabel || '').trim() || `${cleanId}-UAC`;
        setSelectedClientIds(prev => [...prev, cleanId]);
        setClientLabels(prev => ({ ...prev, [cleanId]: label }));
        setNewClientId('');
        setNewClientLabel('');
    };

    // Remover ID de cliente
    const handleRemoveClient = (clientId) => {
        setSelectedClientIds(prev => prev.filter(id => id !== clientId));
        setClientLabels(prev => {
            const copy = { ...prev };
            delete copy[clientId];
            return copy;
        });
    };

    // Restaurar padrões
    const handleRestoreDefaults = () => {
        if (!window.confirm("Deseja restaurar as 12 unidades Acads padrão?")) return;
        setSelectedClientIds(DEFAULT_ACADS.map(a => a.clientId));
        setClientLabels(DEFAULT_ACADS.reduce((acc, a) => ({ ...acc, [a.clientId]: a.label }), {}));
    };

    // Salvar configuração no Firestore
    const handleSaveConfig = async () => {
        try {
            setSavingConfig(true);
            const ref = doc(db, `${DB_PATH}hub_settings`, 'internal_team_config');
            const payload = {
                selectedClientIds,
                clientLabels,
                updatedAt: new Date().toISOString(),
                updatedBy: currentUser?.email || 'Administrador'
            };
            await setDoc(ref, payload, { merge: true });
            alert("Configuração de Equipe Interna (Acads) salva com sucesso!");
        } catch (err) {
            console.error("Erro ao salvar configuração:", err);
            alert("Erro ao salvar configuração: " + err.message);
        } finally {
            setSavingConfig(false);
        }
    };

    // Disparar sincronização manual
    const handleTriggerSync = async () => {
        if (selectedClientIds.length === 0) {
            return alert("Configure ao menos um código de cliente de Acad antes de sincronizar.");
        }

        if (!window.confirm(`Deseja sincronizar agora os dados cadastrais e salários dos colaboradores das ${selectedClientIds.length} unidades Acads cadastradas?`)) {
            return;
        }

        setIsSyncing(true);
        setSyncProgress({ percent: 5, status: 'Iniciando conexão com Alterdata...' });
        setSyncResult(null);

        try {
            const result = await sincronizarEquipeInternaAcads({
                db,
                DB_PATH,
                currentUser,
                onProgress: (p) => setSyncProgress(p)
            });

            setSyncResult(result);
        } catch (err) {
            console.error("Falha ao sincronizar equipe interna:", err);
            alert("Erro na sincronização: " + err.message);
        } finally {
            setIsSyncing(false);
        }
    };

    const themeCard = isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-gray-200';
    const themeInput = `h-9 text-xs rounded-lg border px-3 transition-colors ${
        isDarkMode ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-500' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'
    }`;

    if (loadingConfig) {
        return (
            <div className="p-12 flex flex-col items-center justify-center gap-3 text-slate-400">
                <Loader2 className="animate-spin text-indigo-500" size={32} />
                <p className="text-xs font-bold">Carregando configurações de Equipe Interna...</p>
            </div>
        );
    }

    return (
        <div className="p-6 overflow-y-auto custom-scrollbar space-y-6">
            
            {/* Cabeçalho do Módulo */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-4 dark:border-slate-700">
                <div>
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <Users className="text-indigo-500" size={22} />
                        Configurações de Equipe Interna (Acads - Alterdata)
                    </h3>
                    <p className="text-xs opacity-70 mt-1">
                        Defina quais códigos de cliente (<code className="font-bold text-indigo-400">idDoCliente</code> / código externo) no Alterdata Pack representam unidades do Grupo Acad para busca detalhada de colaboradores e <b>salário base atual</b>.
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <Button
                        onClick={handleTriggerSync}
                        disabled={isSyncing}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs shadow-md"
                        icon={isSyncing ? Loader2 : RefreshCw}
                    >
                        {isSyncing ? 'Sincronizando...' : 'Sincronizar Equipe Interna Agora'}
                    </Button>
                </div>
            </div>

            {/* Modal / Barra de Progresso durante a Sincronização */}
            {isSyncing && syncProgress && (
                <div className="p-4 rounded-xl border border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 dark:border-indigo-700 animate-pulse">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-bold text-indigo-700 dark:text-indigo-300 flex items-center gap-2">
                            <Loader2 className="animate-spin" size={16} />
                            {syncProgress.status}
                        </span>
                        <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400">
                            {syncProgress.percent || 0}%
                        </span>
                    </div>
                    <div className="w-full bg-indigo-200 dark:bg-indigo-950 rounded-full h-2 overflow-hidden">
                        <div 
                            className="bg-indigo-600 h-2 transition-all duration-300"
                            style={{ width: `${syncProgress.percent || 5}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Resultado da Sincronização Recente */}
            {syncResult && (
                <div className={`p-4 rounded-xl border flex items-start justify-between gap-4 ${
                    syncResult.status === 'SUCESSO' 
                        ? (isDarkMode ? 'bg-green-900/20 border-green-700 text-green-300' : 'bg-green-50 border-green-200 text-green-800')
                        : (isDarkMode ? 'bg-orange-900/20 border-orange-700 text-orange-300' : 'bg-orange-50 border-orange-200 text-orange-800')
                }`}>
                    <div className="flex items-start gap-3">
                        <CheckCircle2 size={20} className="text-green-500 shrink-0 mt-0.5" />
                        <div className="text-xs space-y-1">
                            <p className="font-bold text-sm">Sincronização de Equipe Interna Concluída!</p>
                            <p>
                                Total de colaboradores gravados em <code className="font-bold">acad_colaboradores</code>: <b>{syncResult.totalColaboradores}</b>
                            </p>
                            <p>
                                Acads vinculadas com sucesso: <b>{syncResult.totalAcadsVinculadas}</b> de <b>{syncResult.totalAcadsConfiguradas}</b> configuradas. Duração: <b>{(syncResult.duracaoMs / 1000).toFixed(1)}s</b>.
                            </p>
                            {syncResult.erros?.length > 0 && (
                                <p className="text-orange-500">
                                    Ocorrências em {syncResult.erros.length} unidades (verifique o log para detalhes).
                                </p>
                            )}
                        </div>
                    </div>
                    {onNavigateToLogs && (
                        <button 
                            onClick={onNavigateToLogs}
                            className="text-xs font-bold underline hover:opacity-80 shrink-0"
                        >
                            Ver Histórico de Logs
                        </button>
                    )}
                </div>
            )}

            {/* Painel de Adicionar e Gerenciar Unidades */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Coluna 1: Formulário para Adicionar */}
                <div className={`p-5 rounded-2xl border ${themeCard} space-y-4`}>
                    <div className="flex items-center gap-2 text-sm font-bold text-indigo-500">
                        <Plus size={16} />
                        <h4>Adicionar Código de Cliente Acad</h4>
                    </div>

                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-1 opacity-70">
                            Código do Cliente no Alterdata (idDoCliente / externoid) *
                        </label>
                        <input
                            type="text"
                            value={newClientId}
                            onChange={e => setNewClientId(e.target.value)}
                            placeholder="Ex: 10480"
                            className={`${themeInput} w-full font-mono`}
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider mb-1 opacity-70">
                            Nome Amigável / Identificação da Unidade
                        </label>
                        <input
                            type="text"
                            value={newClientLabel}
                            onChange={e => setNewClientLabel(e.target.value)}
                            placeholder="Ex: 10480-UAC (Itaboraí)"
                            className={`${themeInput} w-full`}
                        />
                    </div>

                    <Button
                        onClick={handleAddClient}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
                        icon={Plus}
                    >
                        Adicionar à Lista de Equipe
                    </Button>

                    <div className="border-t pt-4 dark:border-slate-700 flex flex-col gap-2">
                        <Button
                            variant="secondary"
                            onClick={handleRestoreDefaults}
                            className="w-full text-xs"
                        >
                            Restaurar 12 Unidades Padrão
                        </Button>
                        <Button
                            onClick={handleSaveConfig}
                            disabled={savingConfig}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                            icon={savingConfig ? Loader2 : Save}
                        >
                            {savingConfig ? 'Salvando...' : 'Salvar Alterações no Banco'}
                        </Button>
                    </div>
                </div>

                {/* Coluna 2 e 3: Tabela de Unidades Configuradas */}
                <div className="lg:col-span-2 space-y-3">
                    <div className="flex justify-between items-center">
                        <span className="text-xs font-bold uppercase tracking-wider opacity-70 flex items-center gap-1.5">
                            <Building2 size={14} className="text-indigo-400" />
                            Unidades Acads Selecionadas ({selectedClientIds.length})
                        </span>
                        <span className="text-xs text-indigo-500 font-bold">
                            Destino: <code className="bg-indigo-100 dark:bg-indigo-900/40 px-1.5 py-0.5 rounded">acad_colaboradores</code>
                        </span>
                    </div>

                    <div className="border rounded-2xl overflow-hidden shadow-sm">
                        <table className="w-full text-xs text-left">
                            <thead className={`uppercase font-bold ${
                                isDarkMode ? 'bg-slate-900 text-slate-400 border-b border-slate-700' : 'bg-gray-100 text-gray-600 border-b border-gray-200'
                            }`}>
                                <tr>
                                    <th className="p-3 w-28">Código</th>
                                    <th className="p-3">Unidade / Rótulo de Identificação</th>
                                    <th className="p-3 text-center w-20">Ação</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${
                                isDarkMode ? 'divide-slate-700 bg-slate-800' : 'divide-gray-100 bg-white'
                            }`}>
                                {selectedClientIds.map((clientId) => (
                                    <tr key={clientId} className={isDarkMode ? 'hover:bg-slate-700/40' : 'hover:bg-gray-50'}>
                                        <td className="p-3 font-mono font-bold text-indigo-400">
                                            {clientId}
                                        </td>
                                        <td className="p-3 font-medium">
                                            {clientLabels[clientId] || `${clientId}-UAC`}
                                        </td>
                                        <td className="p-3 text-center">
                                            <button
                                                onClick={() => handleRemoveClient(clientId)}
                                                className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 p-1.5 rounded-lg transition-colors"
                                                title="Remover Unidade"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {selectedClientIds.length === 0 && (
                                    <tr>
                                        <td colSpan="3" className="p-8 text-center opacity-50 italic">
                                            Nenhuma unidade Acad configurada. Clique em "Restaurar 12 Unidades Padrão" ou adicione os códigos no formulário.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Card Explicativo de Regras de Negócio e Dados Armazenados */}
            <div className={`p-4 rounded-xl border ${themeCard} flex items-start gap-3`}>
                <Info size={18} className="text-indigo-400 shrink-0 mt-0.5" />
                <div className="text-xs leading-relaxed space-y-1 opacity-90">
                    <p className="font-bold text-indigo-400">
                        Como funciona o processo de Equipe Interna:
                    </p>
                    <p>
                        • O sistema lê cada código cadastrado acima, localiza o <b>ID interno da empresa</b> na Alterdata através do campo <code className="font-bold">externoid</code> (com fallback de CNPJ).
                    </p>
                    <p>
                        • Em seguida, faz a leitura dos colaboradores ativos pelo endpoint <code className="font-bold">/api/v1/funcionarios</code> e grava os registros completos na coleção <code className="font-bold text-emerald-400">acad_colaboradores</code>.
                    </p>
                    <p>
                        • <b>Dados extraídos:</b> Matrícula, Nome, CPF, Cargo/Função, <b>Salário Base Atualizado</b>, Data de Admissão, Afastamento, Dados Bancários/PIX e Horários contratuais.
                    </p>
                </div>
            </div>

        </div>
    );
};

export default InternalTeamTab;
